import { createSignal, onMount, For, Show, createMemo } from "solid-js";
import { api, type SessionInfo, type AuthSessionInfo, type PlatformAuthProvider } from "../shared";
import { getAdminRuntimeConfig } from "../runtime";
import { RefreshTokensTable } from "./RefreshTokensTable";
import { AuthSessionsTable } from "./AuthSessionsTable";

const PAGE_SIZE = 20;
const PROVIDER_KEYS: Record<string, Array<{ key: string; label: string; sensitive?: boolean }>> = {
  google: [
    { key: "clientId", label: "Client ID" },
    { key: "clientSecret", label: "Client Secret", sensitive: true },
    { key: "redirectUri", label: "Redirect URI" },
  ],
};

export function AuthPanel() {
  const [sessions, setSessions] = createSignal<SessionInfo[]>([]);
  const [sessionsTotal, setSessionsTotal] = createSignal(0);
  const [platformProviders, setPlatformProviders] = createSignal<PlatformAuthProvider[]>([]);
  const [error, setError] = createSignal("");
  const [message, setMessage] = createSignal("");
  const [linkPage, setLinkPage] = createSignal(1);
  const [showProviders, setShowProviders] = createSignal(false);

  const linkPages = createMemo(() => Math.max(1, Math.ceil(sessionsTotal() / PAGE_SIZE)));
  const linkPageRows = createMemo(() => {
    const start = (linkPage() - 1) * PAGE_SIZE;
    return (sessions() || []).slice(start, start + PAGE_SIZE);
  });
  const googleCallbackUrl = createMemo(() => `${getAdminRuntimeConfig().managedAppBaseUrl}/auth/google/callback`);

  async function load() {
    try {
      const s = await api.sessions.list();
      setSessions(s.items || []);
      setSessionsTotal(s.total);
      setPlatformProviders((await api.platformAuth.list()) || []);
    } catch (e: any) {
      setError(e.stack || e.message);
    }
  }

  onMount(load);

  async function revokeLoginLink(id: string) {
    try {
      await api.sessions.revoke(id);
      setMessage("Login link revoked");
      setTimeout(() => setMessage(""), 3000);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div class="space-y-8">
      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-start justify-between">
          <pre class="whitespace-pre-wrap text-xs font-mono">{error()}</pre>
          <button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 shrink-0 cursor-pointer">✕</button>
        </div>
      </Show>
      <Show when={message()}>
        <div class="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg text-sm">{message()}</div>
      </Show>

      {/* Auth Sessions — first */}
      <section class="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div class="mb-2 flex items-center justify-between gap-3">
          <h2 class="text-sm font-semibold text-gray-200">Google OAuth Setup</h2>
          <span class="rounded border border-cyan-800 bg-cyan-950/40 px-2 py-0.5 text-[10px] font-medium text-cyan-300">
            Callback URI
          </span>
        </div>
        <p class="mb-2 text-xs text-gray-400">
          Use this redirect URI when configuring the Google auth provider for the currently selected dashboard target.
        </p>
        <code class="block overflow-x-auto rounded border border-gray-800 bg-black/30 px-3 py-2 text-xs text-emerald-300">
          {googleCallbackUrl()}
        </code>
      </section>

      <section class="space-y-3">
        <button
          type="button"
          onClick={() => setShowProviders((v) => !v)}
          class="flex w-full items-center justify-between gap-3 cursor-pointer"
        >
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold">Platform Auth Providers</h2>
            <span class="text-xs text-gray-500">
              <Show when={showProviders()} fallback={<>▼</>}>▲</Show>
            </span>
          </div>
          <span class="text-xs text-gray-500">Fallback settings used when organizations do not override</span>
        </button>
        <Show when={showProviders()}>
          <Show
            when={platformProviders().length > 0}
            fallback={<div class="text-gray-600 text-sm bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">No platform auth providers found</div>}
          >
            <For each={platformProviders()}>
              {(provider) => (
                <PlatformProviderCard
                  provider={provider}
                  onSaved={async () => {
                    setMessage(`${provider.displayName} updated`);
                    setTimeout(() => setMessage(""), 3000);
                    await load();
                  }}
                  onError={(nextError) => setError(nextError)}
                />
              )}
            </For>
          </Show>
        </Show>
      </section>

      <AuthSessionsTable />

      {/* Login Links — second */}
      <section>
        <h2 class="text-lg font-semibold mb-4">One-Time Login Links ({sessions().length})</h2>
        <Show
          when={sessions().length > 0}
          fallback={<div class="text-gray-600 text-sm bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">No active login links</div>}
        >
          <div class="border border-gray-800 rounded-lg overflow-x-auto">
            <div class="max-h-96 overflow-y-auto">
              <table class="w-full text-sm">
                <thead class="bg-gray-900 text-gray-400 sticky top-0">
                  <tr>
                    <th class="text-left px-4 py-3 font-medium">User</th>
                    <th class="text-left px-4 py-3 font-medium">Created</th>
                    <th class="text-left px-4 py-3 font-medium">Expires</th>
                    <th class="text-left px-4 py-3 font-medium">Status</th>
                    <th class="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-800">
                  <For each={linkPageRows()}>
                    {(s) => (
                      <tr class="hover:bg-gray-900/50">
                        <td class="px-4 py-3 text-gray-200">{s.userEmail}</td>
                        <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.createdAt).toLocaleString()}</td>
                        <td class="px-4 py-3 text-gray-500 text-xs">{new Date(s.expiresAt).toLocaleString()}</td>
                        <td class="px-4 py-3">
                          {s.usedAt ? (
                            <span class="text-xs text-gray-500">Used {new Date(s.usedAt).toLocaleString()}</span>
                          ) : new Date(s.expiresAt) < new Date() ? (
                            <span class="text-xs text-red-400">Expired</span>
                          ) : (
                            <span class="text-xs text-emerald-400 flex items-center gap-1"><span class="w-1.5 h-1.5 rounded-full bg-emerald-400" />Active</span>
                          )}
                        </td>
                        <td class="px-4 py-3 text-right">
                          {s.usedAt || s.revokedAt
                            ? <span class="text-xs text-gray-600">—</span>
                            : <button onClick={() => revokeLoginLink(s.id)} class="text-xs px-2 py-1 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                          }
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </div>
          <Show when={linkPages() > 1}>
            <Pagination page={linkPage()} total={linkPages()} onChange={setLinkPage} />
          </Show>
        </Show>
      </section>
      <RefreshTokensTable />
    </div>
  );
}

function PlatformProviderCard(props: {
  provider: PlatformAuthProvider;
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const defs = PROVIDER_KEYS[props.provider.providerName] ?? [];
  const [enabled, setEnabled] = createSignal(!!props.provider.enabled);
  const [saving, setSaving] = createSignal(false);
  const [draft, setDraft] = createSignal<Record<string, string>>(
    Object.fromEntries(props.provider.values.map((item) => [item.key, item.value ?? ""])),
  );

  async function save() {
    setSaving(true);
    props.onError("");
    try {
      await api.platformAuth.update(props.provider.providerId, {
        enabled: enabled() ? 1 : 0,
        config: draft(),
      });
      await props.onSaved();
    } catch (e: any) {
      props.onError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="rounded-lg border border-gray-800 bg-gray-900 p-4">
      <div class="mb-4 flex items-center justify-between gap-3">
        <div>
          <div class="text-sm font-semibold text-gray-200">{props.provider.displayName}</div>
          <div class="text-xs text-gray-500">{props.provider.providerName}</div>
        </div>
        <label class="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={enabled()}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
            class="rounded bg-gray-800 border-gray-700 text-cyan-500"
          />
          Enabled
        </label>
      </div>
      <Show when={defs.length > 0}>
        <div class="space-y-3">
          <For each={defs}>
            {(def) => {
              const current = () => props.provider.values.find((item) => item.key === def.key);
              return (
                <div>
                  <div class="mb-1 flex items-center justify-between gap-3">
                    <label class="text-xs font-medium text-gray-400">{def.label}</label>
                    <Show when={current()?.sensitive && current()?.maskedValue}>
                      <span class="text-[11px] text-gray-500">Current: {current()?.maskedValue}</span>
                    </Show>
                  </div>
                  <input
                    type={def.sensitive ? "password" : "text"}
                    value={draft()[def.key] ?? ""}
                    placeholder={def.sensitive && current()?.hasValue ? "Enter new secret to replace" : ""}
                    onInput={(e) => setDraft((value) => ({ ...value, [def.key]: e.currentTarget.value }))}
                    class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200"
                  />
                </div>
              );
            }}
          </For>
        </div>
      </Show>
      <div class="mt-4 flex justify-end">
        <button
          onClick={save}
          disabled={saving()}
          class="rounded bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 disabled:opacity-50 cursor-pointer"
        >
          {saving() ? "Saving..." : "Save Provider"}
        </button>
      </div>
    </div>
  );
}

function Pagination(props: { page: number; total: number; onChange: (p: number) => void }) {
  return (
    <div class="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-800 bg-gray-900/50">
      <button onClick={() => props.onChange(Math.max(1, props.page - 1))} disabled={props.page === 1} class="px-3 py-1 text-xs bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Prev</button>
      <span class="text-xs text-gray-400">Page {props.page} of {props.total}</span>
      <button onClick={() => props.onChange(Math.min(props.total, props.page + 1))} disabled={props.page >= props.total} class="px-3 py-1 text-xs bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Next</button>
    </div>
  );
}
